import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { countOrACVFormatterMillionFixedOne } from '../../util/customFunctions';
import { updatedScheme10 } from '../../util/chartColorScheme';

/**
 * Paper 7 - Deal Conversion Efficiency
 * Contains 4 charts:
 * 1. Pipeline Aging by Age Buckets (stacked bar - percent)
 * 2. Conversion Cycletime - Qualified to Commit (stacked bar - count)
 * 3. Percent Conversion from Qualification (bar + line chart)
 * 4. Percent Conversion from POC (bar + line chart)
 */

// Stage label for C0002: Qualify (instead of Qualification)
const getQualificationLabel = (company) => (company === 'C0002' ? 'Qualify' : 'Qualification');
const getEvaluationLabel = (company) => (company === 'C0002' ? 'Demo' : 'Evaluation');

// Chart 1: Pipeline Aging by Age Buckets
export const PipelineAgingChart = ({ data, ageBuckets, hideLegend, company }) => {
  const chartRef = useRef(null);

  useEffect(() => {
    if (!data || !chartRef.current || !ageBuckets) return;

    const container = chartRef.current;
    let cleanup = () => {};

    const render = () => {
      const containerWidth = Math.max(0, container.getBoundingClientRect().width);
      if (!containerWidth) return;

      d3.select(container).selectAll('*').remove();

      // Use fixed base dimensions for consistent aspect ratio (match other Deal Conversion charts)
      const baseWidth = 750;
      const baseHeight = 390;
      const aspectRatio = baseHeight / baseWidth;
      const containerHeight = containerWidth * aspectRatio;

      const margin = { top: 40, right: 30, bottom: 60, left: 60 };
      const width = containerWidth - margin.left - margin.right;
      const height = containerHeight - margin.top - margin.bottom;

      const svgWidth = containerWidth;
      const svgHeight = containerHeight;

      const svg = d3
        .select(container)
        .append('svg')
        .attr('width', svgWidth)
        .attr('height', svgHeight)
        .attr('viewBox', `0 0 ${svgWidth} ${svgHeight}`)
        .attr('preserveAspectRatio', 'xMidYMid meet')
        .style('max-width', '100%')
        .style('height', 'auto')
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);

    // Color scheme
    const colorScale = d3.scaleOrdinal(updatedScheme10);

    // Key transformation must match compute output: <6mo→lt6mo, 6-9mo→6to9mo, 9-12mo→9to12mo, >12mo→gt12mo
    const toBucketKey = (bucket) => bucket.replace(/</g, 'lt').replace(/>/g, 'gt').replace(/-/g, 'to').replace(/\+/g, '');

    const colors = {};
    ageBuckets.forEach((bucket) => {
      colors[toBucketKey(bucket)] = colorScale(bucket);
    });

    // X Scale
    const xScale = d3
      .scaleBand()
      .domain(data.map(d => d.month))
      .range([0, width])
      .padding(0.3);

    // Y Scale (0-100%)
    const yScale = d3
      .scaleLinear()
      .domain([0, 100])
      .range([height, 0]);

    // Grid lines
    svg
      .append('g')
      .attr('class', 'grid')
      .call(d3.axisLeft(yScale).ticks(5).tickSize(-width).tickFormat(''))
      .selectAll('line')
      .style('stroke', '#e0e0e0')
      .style('stroke-opacity', 0.7);

    svg.selectAll('.domain').remove();

    // X Axis
    svg
      .append('g')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(xScale).tickSize(0))
      .selectAll('text')
      .style('font-size', '10px');

    // Y Axis
    svg
      .append('g')
      .call(d3.axisLeft(yScale).ticks(5).tickFormat(d => `${d}%`))
      .selectAll('text')
      .style('font-size', '10px');

    // Stack generator with age bucket keys
    const stackKeys = ageBuckets.map(toBucketKey);

    const stack = d3.stack()
      .keys(stackKeys)
      .order(d3.stackOrderNone)
      .offset(d3.stackOffsetNone);

    const series = stack(data);

    // Draw stacked bars
    svg
      .selectAll('g.layer')
      .data(series)
      .enter()
      .append('g')
      .attr('class', 'layer')
      .attr('fill', d => colors[d.key])
      .selectAll('rect')
      .data(d => d)
      .enter()
      .append('rect')
      .attr('x', d => xScale(d.data.month))
      .attr('y', d => yScale(d[1]))
      .attr('height', d => yScale(d[0]) - yScale(d[1]))
      .attr('width', xScale.bandwidth());

    // Add percentage labels on bars (upper line)
    svg
      .selectAll('g.label-layer-percent')
      .data(series)
      .enter()
      .append('g')
      .attr('class', 'label-layer-percent')
      .selectAll('text')
      .data(d => d.map(item => ({ ...item, key: d.key })))
      .enter()
      .append('text')
      .attr('x', d => xScale(d.data.month) + xScale.bandwidth() / 2)
      .attr('y', d => yScale(d[1]) + (yScale(d[0]) - yScale(d[1])) / 2 - 6)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .attr('font-size', '9px')
      .attr('font-weight', 'bold')
      .attr('fill', 'white')
      .text(d => {
        const barHeight = yScale(d[0]) - yScale(d[1]);
        const percentage = d[1] - d[0];
        if (barHeight >= 30 && percentage !== 0) {
          return `${Math.abs(Math.round(percentage))}%`;
        }
        return '';
      });

    // Add count labels on bars (lower line)
    svg
      .selectAll('g.label-layer-count')
      .data(series)
      .enter()
      .append('g')
      .attr('class', 'label-layer-count')
      .selectAll('text')
      .data(d => d.map(item => ({ ...item, key: d.key })))
      .enter()
      .append('text')
      .attr('x', d => xScale(d.data.month) + xScale.bandwidth() / 2)
      .attr('y', d => yScale(d[1]) + (yScale(d[0]) - yScale(d[1])) / 2 + 6)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .attr('font-size', '8px')
      .attr('fill', 'white')
      .text(d => {
        const barHeight = yScale(d[0]) - yScale(d[1]);
        const percentage = d[1] - d[0];
        const countKey = `${d.key}_count`;
        const count = d.data[countKey] || 0;
        if (barHeight >= 30 && percentage !== 0 && count > 0) {
          return `(${count})`;
        }
        return '';
      });

    // Add total count labels on top of each bar
    svg
      .selectAll('.total-label')
      .data(data)
      .enter()
      .append('text')
      .attr('class', 'total-label')
      .attr('x', d => xScale(d.month) + xScale.bandwidth() / 2)
      .attr('y', d => yScale(100) - 5)
      .attr('text-anchor', 'middle')
      .attr('font-size', '10px')
      .attr('font-weight', 'bold')
      .attr('fill', '#333')
      .text(d => d.totalCount > 0 ? d.totalCount : '');

    // Title - Line 1 (C0002: use "Qualify" instead of "Qualification")
    const qualLabelPipeline = getQualificationLabel(company);
    svg
      .append('text')
      .attr('x', width / 2)
      .attr('y', -25)
      .attr('text-anchor', 'middle')
      .attr('font-size', '13px')
      .attr('font-weight', 'bold')
      .text(`Qualified Pipeline Aging (since ${qualLabelPipeline})`);
    // Title - Line 2
    svg
      .append('text')
      .attr('x', width / 2)
      .attr('y', -10)
      .attr('text-anchor', 'middle')
      .attr('font-size', '13px')
      .attr('font-weight', 'bold')
      .text('% of # of opps - at start of each month');

    // Legend
    if (!hideLegend) {
      const legendSpacing = 80;
      const legendStartX = (width - ageBuckets.length * legendSpacing) / 2;
      const legend = svg
        .append('g')
        .attr('class', 'legend')
        .attr('transform', `translate(${legendStartX}, ${height + 35})`);

      ageBuckets.forEach((bucket, i) => {
        const legendItem = legend
          .append('g')
          .attr('transform', `translate(${i * legendSpacing}, 0)`);

        legendItem
          .append('rect')
          .attr('x', 0)
          .attr('y', -6)
          .attr('width', 12)
          .attr('height', 12)
          .attr('fill', colorScale(bucket));

        legendItem
          .append('text')
          .attr('x', 18)
          .attr('y', 4)
          .attr('font-size', '11px')
          .attr('fill', '#333')
          .text(bucket);
      });
    }
    };

    render();

    const ro = new ResizeObserver(() => render());
    ro.observe(container);
    cleanup = () => ro.disconnect();

    return cleanup;

  }, [data, ageBuckets, hideLegend, company]);

  return <div ref={chartRef} id="printChart" style={{ width: '100%', overflow: 'hidden' }} />;
};

// Chart 2: Conversion Cycletime - Qualified to Commit
export const ConversionCycletimeChart = ({ data, timeBuckets, hideLegend, company }) => {
  const chartRef = useRef(null);

  useEffect(() => {
    if (!data || !chartRef.current || !timeBuckets) return;

    const container = chartRef.current;
    let cleanup = () => {};

    const render = () => {
      const containerWidth = Math.max(0, container.getBoundingClientRect().width);
      if (!containerWidth) return;

      d3.select(container).selectAll('*').remove();

      // Use fixed base dimensions for consistent aspect ratio
      const baseWidth = 750;
      const baseHeight = 390;
      const aspectRatio = baseHeight / baseWidth;
      const containerHeight = containerWidth * aspectRatio;

      const margin = { top: 40, right: 30, bottom: 60, left: 60 };
      const width = containerWidth - margin.left - margin.right;
      const height = containerHeight - margin.top - margin.bottom;

      const svgWidth = containerWidth;
      const svgHeight = containerHeight;

      const svg = d3
        .select(container)
        .append('svg')
        .attr('width', svgWidth)
        .attr('height', svgHeight)
        .attr('viewBox', `0 0 ${svgWidth} ${svgHeight}`)
        .attr('preserveAspectRatio', 'xMidYMid meet')
        .style('max-width', '100%')
        .style('height', 'auto')
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);

    // Color scheme
    const colorScale = d3.scaleOrdinal(updatedScheme10);

    // Key transformation must match compute output: <3mo→lt3mo, 3-6mo→3to6mo, 6-9mo→6to9mo, >9mo→gt9mo
    const toBucketKey = (bucket) => bucket.replace(/</g, 'lt').replace(/>/g, 'gt').replace(/-/g, 'to').replace(/\+/g, '');

    const colors = {};
    timeBuckets.forEach((bucket) => {
      colors[toBucketKey(bucket)] = colorScale(bucket);
    });

    // X Scale
    const xScale = d3
      .scaleBand()
      .domain(data.map(d => d.month))
      .range([0, width])
      .padding(0.3);

    // Y Scale - based on max total count
    const maxCount = d3.max(data, d => d.totalCount) * 1.1 || 100;
    const yScale = d3
      .scaleLinear()
      .domain([0, maxCount])
      .range([height, 0]);

    // Grid lines
    svg
      .append('g')
      .attr('class', 'grid')
      .call(d3.axisLeft(yScale).ticks(5).tickSize(-width).tickFormat(''))
      .selectAll('line')
      .style('stroke', '#e0e0e0')
      .style('stroke-opacity', 0.7);

    svg.selectAll('.domain').remove();

    // X Axis
    svg
      .append('g')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(xScale).tickSize(0))
      .selectAll('text')
      .style('font-size', '10px');

    // Y Axis
    svg
      .append('g')
      .call(d3.axisLeft(yScale).ticks(5).tickFormat(d => Math.round(d)))
      .selectAll('text')
      .style('font-size', '10px');

    // Stack generator with time bucket keys
    const stackKeys = timeBuckets.map(toBucketKey);

    const stack = d3.stack()
      .keys(stackKeys)
      .order(d3.stackOrderNone)
      .offset(d3.stackOffsetNone);

    const series = stack(data);

    // Draw stacked bars
    svg
      .selectAll('g.layer')
      .data(series)
      .enter()
      .append('g')
      .attr('class', 'layer')
      .attr('fill', d => colors[d.key])
      .selectAll('rect')
      .data(d => d)
      .enter()
      .append('rect')
      .attr('x', d => xScale(d.data.month))
      .attr('y', d => yScale(d[1]))
      .attr('height', d => yScale(d[0]) - yScale(d[1]))
      .attr('width', xScale.bandwidth());

    // Add count labels on bars
    svg
      .selectAll('g.label-layer')
      .data(series)
      .enter()
      .append('g')
      .attr('class', 'label-layer')
      .selectAll('text')
      .data(d => d)
      .enter()
      .append('text')
      .attr('x', d => xScale(d.data.month) + xScale.bandwidth() / 2)
      .attr('y', d => yScale(d[1]) + (yScale(d[0]) - yScale(d[1])) / 2)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .attr('font-size', '10px')
      .attr('font-weight', 'bold')
      .attr('fill', 'white')
      .text(d => {
        const barHeight = yScale(d[0]) - yScale(d[1]);
        const count = d[1] - d[0];
        if (barHeight >= 20 && count !== 0) {
          return Math.abs(Math.round(count));
        }
        return '';
      });

    // Add total value labels on top of each bar
    svg
      .selectAll('.total-label')
      .data(data)
      .enter()
      .append('text')
      .attr('class', 'total-label')
      .attr('x', d => xScale(d.month) + xScale.bandwidth() / 2)
      .attr('y', d => {
        const totalY = yScale(d.totalCount || 0);
        return totalY - 5; // Position 5px above the top of the bar
      })
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('font-weight', 'bold')
      .attr('fill', '#333')
      .text(d => {
        const total = d.totalCount || 0;
        return total > 0 ? Math.round(total) : '';
      });

    // Title - Line 1 (C0002: use "Qualify" instead of "Qualification")
    const qualLabelCycletime = getQualificationLabel(company);
    svg
      .append('text')
      .attr('x', width / 2)
      .attr('y', -25)
      .attr('text-anchor', 'middle')
      .attr('font-size', '13px')
      .attr('font-weight', 'bold')
      .text(`Conversion Cycle Time - ${qualLabelCycletime} to Closed Won`);
    // Title - Line 2
    svg
      .append('text')
      .attr('x', width / 2)
      .attr('y', -10)
      .attr('text-anchor', 'middle')
      .attr('font-size', '13px')
      .attr('font-weight', 'bold')
      .text('# of opps');

    // Legend
    if (!hideLegend) {
      const legendSpacing = 80;
      const legendStartX = (width - timeBuckets.length * legendSpacing) / 2;
      const legend = svg
        .append('g')
        .attr('class', 'legend')
        .attr('transform', `translate(${legendStartX}, ${height + 35})`);

      timeBuckets.forEach((bucket, i) => {
        const legendItem = legend
          .append('g')
          .attr('transform', `translate(${i * legendSpacing}, 0)`);

        legendItem
          .append('rect')
          .attr('x', 0)
          .attr('y', -6)
          .attr('width', 12)
          .attr('height', 12)
          .attr('fill', colorScale(bucket));

        legendItem
          .append('text')
          .attr('x', 18)
          .attr('y', 4)
          .attr('font-size', '11px')
          .attr('fill', '#333')
          .text(bucket);
      });
    }
    };

    render();
    const ro = new ResizeObserver(() => render());
    ro.observe(container);
    cleanup = () => ro.disconnect();
    return cleanup;

  }, [data, timeBuckets, hideLegend, company]);

  return <div ref={chartRef} id="printChart" style={{ width: '100%', overflow: 'hidden' }} />;
};

// Chart 3: Percent Conversion from Qualification
export const QualificationConversionChart = ({ data, target, company }) => {
  const chartRef = useRef(null);

  useEffect(() => {
    if (!data || !chartRef.current) return;

    const container = chartRef.current;
    let cleanup = () => {};

    const render = () => {
      const containerWidth = Math.max(0, container.getBoundingClientRect().width);
      if (!containerWidth) return;

      d3.select(container).selectAll('*').remove();

      // Use fixed base dimensions for consistent aspect ratio
      const baseWidth = 750;
      const baseHeight = 390;
      const aspectRatio = baseHeight / baseWidth;
      const containerHeight = containerWidth * aspectRatio;

      const margin = { top: 40, right: 30, bottom: 60, left: 60 };
      const width = containerWidth - margin.left - margin.right;
      const height = containerHeight - margin.top - margin.bottom;

      const svgWidth = containerWidth;
      const svgHeight = containerHeight;

      const svg = d3
        .select(container)
        .append('svg')
        .attr('width', svgWidth)
        .attr('height', svgHeight)
        .attr('viewBox', `0 0 ${svgWidth} ${svgHeight}`)
        .attr('preserveAspectRatio', 'xMidYMid meet')
        .style('max-width', '100%')
        .style('height', 'auto')
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);

    // X Scale
    const xScale = d3
      .scaleBand()
      .domain(data.map(d => d.month))
      .range([0, width])
      .padding(0.3);

    // Y Scale
    const maxValue = Math.max(target || 100, d3.max(data, d => d.winRate) || 0, d3.max(data, d => d.cumulativeWinRate) || 0) * 1.2;
    const yScale = d3
      .scaleLinear()
      .domain([0, maxValue])
      .range([height, 0]);

    // Grid lines
    svg
      .append('g')
      .attr('class', 'grid')
      .call(d3.axisLeft(yScale).ticks(5).tickSize(-width).tickFormat(''))
      .selectAll('line')
      .style('stroke', '#e0e0e0')
      .style('stroke-opacity', 0.7);

    svg.selectAll('.domain').remove();

    // X Axis
    svg
      .append('g')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(xScale).tickSize(0))
      .selectAll('text')
      .style('font-size', '10px');

    // Y Axis
    svg
      .append('g')
      .call(d3.axisLeft(yScale).ticks(5).tickFormat(d => `${d}%`))
      .selectAll('text')
      .style('font-size', '10px');

    // Draw blue bars (monthly win rate)
    svg
      .selectAll('.bar')
      .data(data.filter(d => d.totalClosed > 0))
      .enter()
      .append('rect')
      .attr('class', 'bar')
      .attr('x', d => xScale(d.month))
      .attr('y', d => yScale(d.winRate))
      .attr('width', xScale.bandwidth())
      .attr('height', d => height - yScale(d.winRate))
      .attr('fill', '#70ad47');

    // Value labels on bars
    svg
      .selectAll('.bar-label')
      .data(data.filter(d => d.totalClosed > 0 && d.winRate > 0))
      .enter()
      .append('text')
      .attr('class', 'bar-label')
      .attr('x', d => xScale(d.month) + xScale.bandwidth() / 2)
      .attr('y', d => yScale(d.winRate) - 5)
      .attr('text-anchor', 'middle')
      .attr('font-size', '9px')
      .attr('font-weight', 'bold')
      .attr('fill', '#333')
      .text(d => `${d.winRate}%`);

    // Filter data with valid cumulative values for the line
    const validData = data.filter(d => d.totalClosed > 0 && d.cumulativeWinRate > 0);

    if (validData.length > 0) {
      // Line generator for cumulative win rate (orange)
      const line = d3
        .line()
        .x(d => xScale(d.month) + xScale.bandwidth() / 2)
        .y(d => yScale(d.cumulativeWinRate))
        .curve(d3.curveMonotoneX);

      // Draw cumulative line
      svg
        .append('path')
        .datum(validData)
        .attr('fill', 'none')
        .attr('stroke', '#ff9800')
        .attr('stroke-width', 2.5)
        .attr('d', line);

      // Draw points on cumulative line
      svg
        .selectAll('.cum-point')
        .data(validData)
        .enter()
        .append('circle')
        .attr('class', 'cum-point')
        .attr('cx', d => xScale(d.month) + xScale.bandwidth() / 2)
        .attr('cy', d => yScale(d.cumulativeWinRate))
        .attr('r', 3)
        .attr('fill', '#ff9800')
        .attr('stroke', '#fff')
        .attr('stroke-width', 1.5);
    }

    // Target line (gold)
    if (target > 0) {
      svg
        .append('line')
        .attr('x1', 0)
        .attr('x2', width)
        .attr('y1', yScale(target))
        .attr('y2', yScale(target))
        .attr('stroke', '#bf9000')
        .attr('stroke-width', 2)
        .attr('stroke-dasharray', '5,5');

      // Target label
      svg
        .append('text')
        .attr('x', width - 5)
        .attr('y', yScale(target) - 5)
        .attr('text-anchor', 'end')
        .attr('font-size', '10px')
        .attr('font-weight', 'bold')
        .attr('fill', '#bf9000')
        .text('Target');
    }

    // Title - Line 1 (C0002: use "Qualify" instead of "Qualification")
    const qualLabelWinRate = getQualificationLabel(company);
    svg
      .append('text')
      .attr('x', width / 2)
      .attr('y', -25)
      .attr('text-anchor', 'middle')
      .attr('font-size', '13px')
      .attr('font-weight', 'bold')
      .text(`Win Rate from ${qualLabelWinRate}`);
    // Title - Line 2
    svg
      .append('text')
      .attr('x', width / 2)
      .attr('y', -10)
      .attr('text-anchor', 'middle')
      .attr('font-size', '13px')
      .attr('font-weight', 'bold')
      .text('# of opps Closed Won as % of all closed opps');

    // Legend
    const qualLegendData = [
      { label: 'Monthly', color: '#70ad47', type: 'rect' },
      { label: 'Cumulative YTD', color: '#ff9800', type: 'line' },
      { label: 'Target', color: '#bf9000', type: 'dash' }
    ];
    const qualLegend = svg
      .append('g')
      .attr('class', 'legend')
      .attr('transform', `translate(${width / 2 - 150}, ${height + 35})`);

    qualLegendData.forEach((item, i) => {
      const legendItem = qualLegend
        .append('g')
        .attr('transform', `translate(${i * 110}, 0)`);

      if (item.type === 'rect') {
        legendItem
          .append('rect')
          .attr('x', 0)
          .attr('y', -6)
          .attr('width', 12)
          .attr('height', 12)
          .attr('fill', item.color);
      } else if (item.type === 'line') {
        legendItem
          .append('line')
          .attr('x1', 0)
          .attr('x2', 12)
          .attr('y1', 0)
          .attr('y2', 0)
          .attr('stroke', item.color)
          .attr('stroke-width', 2.5);
        legendItem
          .append('circle')
          .attr('cx', 6)
          .attr('cy', 0)
          .attr('r', 3)
          .attr('fill', item.color);
      } else if (item.type === 'dash') {
        legendItem
          .append('line')
          .attr('x1', 0)
          .attr('x2', 12)
          .attr('y1', 0)
          .attr('y2', 0)
          .attr('stroke', item.color)
          .attr('stroke-width', 2)
          .attr('stroke-dasharray', '3,2');
      }

      legendItem
        .append('text')
        .attr('x', 18)
        .attr('y', 4)
        .attr('font-size', '11px')
        .attr('fill', '#333')
        .text(item.label);
    });
    };

    render();
    const ro = new ResizeObserver(() => render());
    ro.observe(container);
    cleanup = () => ro.disconnect();
    return cleanup;

  }, [data, target, company]);

  return <div ref={chartRef} id="printChart" style={{ width: '100%', overflow: 'hidden' }} />;
};

// Chart 4: Percent Conversion from POC
export const POCConversionChart = ({ data, target, company }) => {
  const chartRef = useRef(null);

  useEffect(() => {
    if (!data || !chartRef.current) return;

    const container = chartRef.current;
    let cleanup = () => {};

    const render = () => {
      const containerWidth = Math.max(0, container.getBoundingClientRect().width);
      if (!containerWidth) return;

      d3.select(container).selectAll('*').remove();

      // Use fixed base dimensions for consistent aspect ratio
      const baseWidth = 750;
      const baseHeight = 390;
      const aspectRatio = baseHeight / baseWidth;
      const containerHeight = containerWidth * aspectRatio;

      const margin = { top: 40, right: 30, bottom: 60, left: 60 };
      const width = containerWidth - margin.left - margin.right;
      const height = containerHeight - margin.top - margin.bottom;

      const svgWidth = containerWidth;
      const svgHeight = containerHeight;

      const svg = d3
        .select(container)
        .append('svg')
        .attr('width', svgWidth)
        .attr('height', svgHeight)
        .attr('viewBox', `0 0 ${svgWidth} ${svgHeight}`)
        .attr('preserveAspectRatio', 'xMidYMid meet')
        .style('max-width', '100%')
        .style('height', 'auto')
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);

    // X Scale
    const xScale = d3
      .scaleBand()
      .domain(data.map(d => d.month))
      .range([0, width])
      .padding(0.3);

    // Y Scale
    const maxValue = Math.max(target || 100, d3.max(data, d => d.winRate) || 0, d3.max(data, d => d.cumulativeWinRate) || 0) * 1.2;
    const yScale = d3
      .scaleLinear()
      .domain([0, maxValue])
      .range([height, 0]);

    // Grid lines
    svg
      .append('g')
      .attr('class', 'grid')
      .call(d3.axisLeft(yScale).ticks(5).tickSize(-width).tickFormat(''))
      .selectAll('line')
      .style('stroke', '#e0e0e0')
      .style('stroke-opacity', 0.7);

    svg.selectAll('.domain').remove();

    // X Axis
    svg
      .append('g')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(xScale).tickSize(0))
      .selectAll('text')
      .style('font-size', '10px');

    // Y Axis
    svg
      .append('g')
      .call(d3.axisLeft(yScale).ticks(5).tickFormat(d => `${d}%`))
      .selectAll('text')
      .style('font-size', '10px');

    // Draw blue bars (monthly win rate)
    svg
      .selectAll('.bar')
      .data(data.filter(d => d.totalClosed > 0))
      .enter()
      .append('rect')
      .attr('class', 'bar')
      .attr('x', d => xScale(d.month))
      .attr('y', d => yScale(d.winRate))
      .attr('width', xScale.bandwidth())
      .attr('height', d => height - yScale(d.winRate))
      .attr('fill', '#70ad47');

    // Value labels on bars
    svg
      .selectAll('.bar-label')
      .data(data.filter(d => d.totalClosed > 0 && d.winRate > 0))
      .enter()
      .append('text')
      .attr('class', 'bar-label')
      .attr('x', d => xScale(d.month) + xScale.bandwidth() / 2)
      .attr('y', d => yScale(d.winRate) - 5)
      .attr('text-anchor', 'middle')
      .attr('font-size', '9px')
      .attr('font-weight', 'bold')
      .attr('fill', '#333')
      .text(d => `${d.winRate}%`);

    // Filter data with valid cumulative values for the line
    const validData = data.filter(d => d.totalClosed > 0 && d.cumulativeWinRate > 0);

    if (validData.length > 0) {
      // Line generator for cumulative win rate (orange)
      const line = d3
        .line()
        .x(d => xScale(d.month) + xScale.bandwidth() / 2)
        .y(d => yScale(d.cumulativeWinRate))
        .curve(d3.curveMonotoneX);

      // Draw cumulative line
      svg
        .append('path')
        .datum(validData)
        .attr('fill', 'none')
        .attr('stroke', '#ff9800')
        .attr('stroke-width', 2.5)
        .attr('d', line);

      // Draw points on cumulative line
      svg
        .selectAll('.cum-point')
        .data(validData)
        .enter()
        .append('circle')
        .attr('class', 'cum-point')
        .attr('cx', d => xScale(d.month) + xScale.bandwidth() / 2)
        .attr('cy', d => yScale(d.cumulativeWinRate))
        .attr('r', 3)
        .attr('fill', '#ff9800')
        .attr('stroke', '#fff')
        .attr('stroke-width', 1.5);
    }

    // Target line (gold)
    if (target > 0) {
      svg
        .append('line')
        .attr('x1', 0)
        .attr('x2', width)
        .attr('y1', yScale(target))
        .attr('y2', yScale(target))
        .attr('stroke', '#bf9000')
        .attr('stroke-width', 2)
        .attr('stroke-dasharray', '5,5');

      // Target label
      svg
        .append('text')
        .attr('x', width - 5)
        .attr('y', yScale(target) - 5)
        .attr('text-anchor', 'end')
        .attr('font-size', '10px')
        .attr('font-weight', 'bold')
        .attr('fill', '#bf9000')
        .text('Target');
    }

    // Title - Line 1 (C0002: use "Demo" instead of "Evaluation")
    const evalLabel = getEvaluationLabel(company);
    svg
      .append('text')
      .attr('x', width / 2)
      .attr('y', -25)
      .attr('text-anchor', 'middle')
      .attr('font-size', '13px')
      .attr('font-weight', 'bold')
      .text(`Win Rate from ${evalLabel}`);
    // Title - Line 2
    svg
      .append('text')
      .attr('x', width / 2)
      .attr('y', -10)
      .attr('text-anchor', 'middle')
      .attr('font-size', '13px')
      .attr('font-weight', 'bold')
      .text('# of Closed Won opps as % of all closed opps');

    // Legend
    const pocLegendData = [
      { label: 'Monthly', color: '#70ad47', type: 'rect' },
      { label: 'Cumulative YTD', color: '#ff9800', type: 'line' },
      { label: 'Target', color: '#bf9000', type: 'dash' }
    ];
    const pocLegend = svg
      .append('g')
      .attr('class', 'legend')
      .attr('transform', `translate(${width / 2 - 150}, ${height + 35})`);

    pocLegendData.forEach((item, i) => {
      const legendItem = pocLegend
        .append('g')
        .attr('transform', `translate(${i * 110}, 0)`);

      if (item.type === 'rect') {
        legendItem
          .append('rect')
          .attr('x', 0)
          .attr('y', -6)
          .attr('width', 12)
          .attr('height', 12)
          .attr('fill', item.color);
      } else if (item.type === 'line') {
        legendItem
          .append('line')
          .attr('x1', 0)
          .attr('x2', 12)
          .attr('y1', 0)
          .attr('y2', 0)
          .attr('stroke', item.color)
          .attr('stroke-width', 2.5);
        legendItem
          .append('circle')
          .attr('cx', 6)
          .attr('cy', 0)
          .attr('r', 3)
          .attr('fill', item.color);
      } else if (item.type === 'dash') {
        legendItem
          .append('line')
          .attr('x1', 0)
          .attr('x2', 12)
          .attr('y1', 0)
          .attr('y2', 0)
          .attr('stroke', item.color)
          .attr('stroke-width', 2)
          .attr('stroke-dasharray', '3,2');
      }

      legendItem
        .append('text')
        .attr('x', 18)
        .attr('y', 4)
        .attr('font-size', '11px')
        .attr('fill', '#333')
        .text(item.label);
    });
    };

    render();
    const ro = new ResizeObserver(() => render());
    ro.observe(container);
    cleanup = () => ro.disconnect();
    return cleanup;

  }, [data, target, company]);

  return <div ref={chartRef} id="printChart" style={{ width: '100%', overflow: 'hidden' }} />;
};

// Combined component for all 4 charts
const DealConversionCharts = ({ data }) => {
  if (!data) return null;

  return {
    PipelineAgingChart: (
      <PipelineAgingChart
        data={data.pipelineAging}
        ageBuckets={data.ageBuckets}
      />
    ),
    ConversionCycletimeChart: (
      <ConversionCycletimeChart
        data={data.conversionCycletime}
        timeBuckets={data.timeBuckets}
      />
    ),
    QualificationConversionChart: (
      <QualificationConversionChart
        data={data.qualificationConversion}
        target={data.qualificationTarget}
      />
    ),
    POCConversionChart: (
      <POCConversionChart
        data={data.pocConversion}
        target={data.pocTarget}
      />
    )
  };
};

export default DealConversionCharts;
